import React, { useState } from "react";
import Layout from "../../Layout";
import "./project_case.css";

import Student_Banner from "../../Images/StudentBanner.png";

export default function Student() {
  const [active, setActive] = useState("discover");

  function showDiscover() {
    setActive("discover");
  }
  function showWireframes() {
    setActive("wireframes");
  }
  function showDev() {
    setActive("dev");
  }
  function showFinal() {
    setActive("final");
  }

  return (
    <Layout>
      <main className="cs-page">
        {/* 1) FULL WIDTH HERO IMAGE */}
        <section className="cs-hero">
          <img src={Student_Banner} alt="Student Management System Banner" />
        </section>

        {/* 2) TITLE / TAGLINE / META */}
        <section className="cs-block">
          <h1 className="cs-title">
            {" "}
            <u>Software Engineer Project - Student Management System </u>
          </h1>
          <p className="cs-tagline">
            My introduction to building software in a team—combining UML
            diagrams, structured planning, and practical coding to deliver a
            working student management tool.
          </p>

          <div className="cs-divider" />

          <div className="cs-meta">
            <div className="cs-meta-item">
              <h3 className="cs-meta-label">Timeline</h3>
              <p className="cs-meta-text">January 2024 – May 2024</p>
            </div>
            <div className="cs-meta-item">
              <h3 className="cs-meta-label">Programs Used</h3>
              <p className="cs-meta-text">
                Java, JavaFX, Maven, Python, JUnit, Git + GitHub
              </p>
            </div>
          </div>
          <div className="cs-divider" />
        </section>

        {/* 3) ABOUT THE PROJECT */}
        <section className="cs-block">
          <h2 className="cs-h2">About the Project</h2>
          <p className="cs-tagline">
            This Student Management System was a semester-long group project
            where our team collaborated to design and build a functional
            application from the ground up. We began by creating detailed UML
            diagrams—use case diagrams, sequence diagrams, and class diagrams—to
            clearly map out system behavior before touching the code. Working
            together, we implemented features such as student registration,
            course management, and user authentication while ensuring that the
            system remained organized and easy to navigate. This project
            strengthened my ability to communicate ideas, divide
            responsibilities, and combine individual contributions into a
            cohesive final product. It was a meaningful introduction to building
            real software collaboratively, where planning, structure, and
            teamwork mattered just as much as the code itself.
          </p>
        </section>

        <div className="cs-divider" />

        {/* 4) MY ROLE + ACCOMPLISHMENTS */}
        <section className="cs-block">
          <h2 className="cs-h2">My Role</h2>
          <p className="cs-tagline">
            I collaborated with my team to design and build this Student
            Management System from the ground up. My role focused on translating
            requirements into detailed UML diagrams, structuring the system’s
            logic, and implementing key features in Java while ensuring that our
            work stayed organized, efficient, and aligned with software
            engineering best practices.
          </p>

          <div className="cs-card">
            <div className="cs-item">
              <span className="cs-item-title">
                UML Modeling & System Planning:
              </span>
              <span className="cs-item-text">
                Created use case diagrams, sequence diagrams, and class diagrams
                to map system behavior and clarify responsibilities before
                development began.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">
                Software Engineering Mindset:
              </span>
              <span className="cs-item-text">
                Learned to break down requirements, design modular classes, and
                follow clean architecture principles to build maintainable and
                scalable code.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">
                Team Collaboration with Git/GitHub:
              </span>
              <span className="cs-item-text">
                Collaborated through Git branching, pull requests, and version
                control to coordinate features smoothly and resolve merge
                conflicts as a team.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">Java Development:</span>
              <span className="cs-item-text">
                Implemented core system features in Java, including student
                registration, course management, object-oriented logic, and data
                handling.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">
                Team Coordination & Communication:
              </span>
              <span className="cs-item-text">
                Worked closely with teammates to divide tasks, review code, and
                ensure the final system functioned cohesively from front end to
                backend logic.
              </span>
            </div>
          </div>
        </section>

        <div className="cs-divider" />

        {/* 6) MY PROCESS: MENU (left) + PANEL (right) */}
        <section className="cs-block">
          <h2 className="cs-h2">My Process</h2>

          <div className="cs-process">
            <div className="cs-menu">
              <button
                className={
                  "cs-menu-item" + (active === "discover" ? " is-active" : "")
                }
                onMouseEnter={showDiscover}
              >
                Project Overview
              </button>

              <button
                className={
                  "cs-menu-item" + (active === "wireframes" ? " is-active" : "")
                }
                onMouseEnter={showWireframes}
              >
                Collaboration
              </button>

              <button
                className={
                  "cs-menu-item" + (active === "dev" ? " is-active" : "")
                }
                onMouseEnter={showDev}
              >
                UML & System Design
              </button>

              <button
                className={
                  "cs-menu-item" + (active === "final" ? " is-active" : "")
                }
                onMouseEnter={showFinal}
              >
                Project Reflection
              </button>
            </div>

            <div className="cs-panel">
              {/* PROJECT OVERVIEW */}
              <div
                className="cs-pane"
                style={{ display: active === "discover" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Project Overview</u>
                </h3>

                <p className="cs-tagline">
                  The Student Management System was assigned as our
                  semester-long software engineering project, where the goal was
                  to build a fully functioning system that could manage
                  students, instructors, classes, and related academic records.
                  Before touching any code, my team and I reviewed the
                  assignment requirements and broke them down into clear
                  functional modules, ensuring we understood the full scope of
                  what we needed to deliver.
                </p>

                <p className="cs-tagline">
                  I began by sketching out UML diagrams—use case diagrams, class
                  diagrams, and sequence diagrams—to map the system’s
                  architecture and define how each component would communicate.
                  These diagrams helped us think like software engineers,
                  focusing on data flow, object relationships, and maintainable
                  structures. This early planning became the foundational
                  blueprint we followed throughout the project.
                </p>

                <p className="cs-tagline">
                  Once the design was finalized, we planned the workflow for how
                  the system should behave end-to-end: how data should be
                  stored, validated, processed, and displayed. This gave us a
                  clear roadmap and kept the development process organized,
                  predictable, and aligned with engineering standards.
                </p>
              </div>

              {/* COLLABORATION / DEVELOPMENT PROCESS */}
              <div
                className="cs-pane"
                style={{ display: active === "wireframes" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Collaboration & Engineering Process</u>
                </h3>

                <p className="cs-tagline">
                  The development process was a hands-on team effort where we
                  collaborated through Git and GitHub to manage branches,
                  resolve conflicts, and review each other’s code. This taught
                  us how to work in a real engineering environment—communicating
                  changes, maintaining clean commit histories, and ensuring the
                  project remained stable as multiple people contributed
                  simultaneously.
                </p>

                <p className="cs-tagline">
                  I played a key role in translating our UML design into Java
                  classes, implementing core logic for managing student records,
                  course assignments, and data validation. Working in Java
                  pushed me to focus on clean object-oriented structure,
                  encapsulation, and ensuring each class had a single, clear
                  responsibility. This improved both code readability and
                  maintainability.
                </p>

                <p className="cs-tagline">
                  Throughout the project, we followed software engineering
                  principles such as modular design, separation of concerns, and
                  iterative refinement. By testing each feature as we built it,
                  we ensured the system stayed functional and aligned with the
                  original requirements.
                </p>
              </div>

              {/* UML & SYSTEM DESIGN */}
              <div
                className="cs-pane"
                style={{ display: active === "dev" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>UML & System Implementation</u>
                </h3>

                <p className="cs-tagline">
                  Converting UML diagrams into working code was one of the most
                  valuable parts of the project. The class diagrams guided how
                  we structured our Java objects—students, courses, instructors,
                  and enrollment logic—while sequence diagrams helped us
                  implement interactions consistently across the system. This
                  experience taught me how theoretical design translates into
                  practical, real-world code.
                </p>

                <p className="cs-tagline">
                  I focused heavily on making sure each feature worked reliably
                  and matched our design specifications. Whether it was building
                  input validation, handling edge cases, or structuring the flow
                  of student data through the system, I gained a deeper
                  understanding of building predictable and scalable software.
                  Working with Java reinforced strong object-oriented habits,
                  and the project helped refine my engineering intuition.
                </p>

                <p className="cs-tagline">
                  As a team, we reviewed each other’s code to ensure consistency
                  and quality. This collaborative process strengthened the final
                  result and gave us a professional workflow similar to what
                  real engineering teams use.
                </p>
              </div>

              {/* FINAL OUTCOME */}
              <div
                className="cs-pane"
                style={{ display: active === "final" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Final Outcome</u>
                </h3>

                <p className="cs-tagline">
                  The final product was a fully functioning Student Management
                  System with organized architecture, clear data flow, and
                  collaboratively written code. It successfully modeled
                  real-world academic processes—from managing student
                  information to handling course enrollments—using the
                  structures we planned from day one.
                </p>

                <p className="cs-tagline">
                  This project strengthened my understanding of software
                  engineering from both a technical and collaborative
                  perspective. It taught me how to transform UML design into
                  code, work effectively in a team, think like an engineer, and
                  deliver a multi-layered system that is both functional and
                  well-structured.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="cs-divider" />

        {/* 7) Learning & Impact */}
        <section className="cs-block">
          <h2 className="cs-h2">Learning & Impact</h2>

          <p className="cs-tagline">
            Working on the Student Management System taught me how to approach
            software engineering from planning to execution. I learned how to
            use UML diagrams to structure complex systems, collaborate
            effectively with teammates, and write clean, object-oriented Java
            code that models real-world processes. This project strengthened
            both my technical foundation and my ability to think like an
            engineer.
          </p>

          <div className="cs-card">
            <div className="cs-item">
              <span className="cs-item-title">UML & System Design:</span>
              <span className="cs-item-text">
                Gained experience translating UML diagrams—use case, class, and
                sequence— into a working software architecture that structured
                the entire project.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">
                Object-Oriented Programming:
              </span>
              <span className="cs-item-text">
                Strengthened my understanding of OOP principles while building
                Java classes that modeled students, instructors, courses, and
                enrollment logic.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">
                Collaboration & Version Control:
              </span>
              <span className="cs-item-text">
                Learned how to collaborate with teammates using Git and
                GitHub—managing branches, resolving merge conflicts, and
                maintaining clean project structure.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">
                Software Engineering Practices:
              </span>
              <span className="cs-item-text">
                Applied engineering fundamentals like modular design, separation
                of concerns, iterative development, and code reviews to keep the
                project organized and maintainable.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">
                Problem-Solving & Debugging:
              </span>
              <span className="cs-item-text">
                Improved my ability to troubleshoot logic issues, validate data,
                and ensure each feature behaved as expected across the system.
              </span>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
}
